import {
  isValidElement,
  type ComponentProps,
  type JSXElementConstructor,
  type ReactElement,
  type ReactNode,
} from "react";

/**
 * Given a React node, get all the children of a node anywhere in the child tree
 * that is of the given type of component
 * @param node the React node to check under
 * @param componentToFind the component to find
 * @param allResults used in recursive calls - this is the aggregation of all
 * the children of the given type
 * @returns all child components under node of the given component type
 */
export const findAllChildrenThatAreComponent = <
  T = any,
  U extends JSXElementConstructor<T> = JSXElementConstructor<T>,
>(
  node: ReactNode,
  componentToFind: U,
  allResults?: ReactElement[]
): Array<ReactElement<ComponentProps<U>>> | undefined => {
  const results = allResults ?? [];

  if (isValidElement(node)) {
    if (node.type === componentToFind) {
      results.push(node);

      return results;
    } else {
      return findAllChildrenThatAreComponent(
        node.props.children,
        componentToFind,
        results
      );
    }
  } else if (
    Array.isArray(node) &&
    node.every((n) => n.type === componentToFind)
  ) {
    allResults?.push(...node);
    return allResults;
  } else {
    return [];
  }
};

/**
 * Get the dimensions of a text node by creating a virtual DOM range
 * @param node the text node (nodeType === 3) @see {@link https://developer.mozilla.org/en-US/docs/Web/API/Node/nodeType}
 * @returns the dimensions of the node (using getBoundingClientRect())
 */
export const getDimensionsOfTextNode = (node: Node): DOMRect | undefined => {
  const range = document.createRange();

  range.selectNodeContents(node);

  return range.getBoundingClientRect?.();
};

/**
 * Get the length of the longest piece of content within a React Element.
 * @param el the React element to look within recursively
 * @returns number in pixels representing the longest piece of content within
 * the queried Element
 */
export const getLargestContentLength = (el?: Element | Node | null): number => {
  if (!el) return 0;

  let largestWidth =
    el.nodeType === 1
      ? Math.max(
          (el as Element).getBoundingClientRect().width,
          (el as Element).scrollWidth
        )
      : el.nodeType === 3
        ? (getDimensionsOfTextNode(el as Node)?.width ?? 0)
        : 0;

  if (!el?.hasChildNodes()) return largestWidth;

  for (const node of el.childNodes) {
    if (node.nodeType === node.ELEMENT_NODE) {
      const largestLengthWithin = getLargestContentLength(node as Element);

      if (largestLengthWithin > largestWidth) {
        largestWidth = largestLengthWithin;
      }
    } else if (node.nodeType === node.TEXT_NODE) {
      const textDims = getDimensionsOfTextNode(node);

      if (textDims && textDims.width > largestWidth) {
        largestWidth = textDims.width;
      }
    }
  }

  return largestWidth;
};

/**
 * Recursively extracts and concatenates text content from a ReactNode tree.
 * @param element The ReactNode to extract text from.
 * @returns The normalized text content from all nested text-bearing children.
 */
export const getTextFromReactNode = (element: ReactNode): string => {
  if (typeof element === "string" || typeof element === "number") {
    return element.toString();
  }

  if (Array.isArray(element)) {
    return element.map((child) => getTextFromReactNode(child)).join("");
  }

  if (isValidElement(element)) {
    return getTextFromReactNode(element.props.children);
  }

  return "";
};

/**
 * Extract the text at the deepest level of a ReactNode.
 * @param element The ReactNode to extract text from.
 * @returns The text at the deepest level of the ReactNode.
 */
export const extractDeepestChildText = (element: ReactNode): string => {
  return getTextFromReactNode(element);
};

/**
 * Extract the text at the deepest level of a DOM Element.
 *
 * @param element The DOM Element to extract text from.
 * @returns The text at the deepest level of the DOM Element.
 */
export const extractDeepestChildTextFromElement = (
  element: Element | null
): string => {
  if (!element) return "";

  let deepestText = "";

  if (element.nodeType === element.TEXT_NODE) {
    deepestText = element.textContent ?? "";
  } else if (element.nodeType === element.ELEMENT_NODE) {
    const children = element.childNodes;

    for (const child of children) {
      const deepestTxtFound = extractDeepestChildTextFromElement(
        child as Element
      );

      if (deepestTxtFound) {
        deepestText = String(deepestTxtFound);
        break;
      }
    }
  }

  return deepestText;
};
